import BaseModel from './BaseModel';
import Project from './Project';
import Change from './Change';
import _ from 'lodash';
import moment from 'moment';
import type { Moment } from 'moment';

export type GoalMilestone = {
    _id: string;
    name: string;
    at: Moment;
};

export type GoalPhase = {
    _id: string;
    name: string;
    startAt?: Moment;
    stopAt?: Moment;
    startMilestone?: string | null;
    stopMilestone?: string | null;
};

/** Class representing a goal. */
class Goal extends BaseModel {
    /**
     * Status constants
     */
    static STATUS_FINISHED  = 'finished';
    static STATUS_RUNNING   = 'running';
    static STATUS_COMING    = 'coming';

    _id: string;
    name: string;
    projects: Project[];
    changes: Change[];
    createdAt: Moment;
    updatedAt: Moment;
    startAt: Moment;
    stopAt: Moment;
    autoAttachProjectChanges: boolean;
    milestones: GoalMilestone[];
    phases: GoalPhase[];

    constructor(properties: AnyObj) {
        super({});

        this._id       = properties._id;
        this.name      = properties.name;
        this.projects  = (properties.projects || []).map((p: AnyObj) => _.isObject(p) ? new Project(p) : new Project({_id: p}));
        this.changes   = (properties.changes || []).map((c: AnyObj) => new Change(c));
        this.createdAt = moment(properties.createdAt);
        this.updatedAt = moment(properties.updatedAt);
        this.startAt   = moment(properties.startAt);
        this.stopAt    = moment(properties.stopAt);
        this.autoAttachProjectChanges = Boolean(properties.autoAttachProjectChanges);
        this.milestones = Goal.sortMilestones((properties.milestones || []).map((m: AnyObj) => ({
            _id: m._id,
            name: m.name,
            at: moment(m.at)
        })));
        this.phases = Goal.sortPhases((properties.phases || []).map((p: AnyObj) => ({
            _id: p._id,
            name: p.name,
            startAt: p.startAt ? moment(p.startAt) : undefined,
            stopAt: p.stopAt ? moment(p.stopAt) : undefined,
            startMilestone: p.startMilestone || null,
            stopMilestone: p.stopMilestone || null
        })), this.milestones);
    }

    /** Sort milestones by date ascending (undated last). */
    static sortMilestones<T extends { name?: string; at?: any }>(milestones: T[] = []): T[] {
        return [...(milestones || [])].sort((a, b) => {
            const aAt = a?.at != null && a.at !== '' ? moment(a.at).valueOf() : Number.POSITIVE_INFINITY;
            const bAt = b?.at != null && b.at !== '' ? moment(b.at).valueOf() : Number.POSITIVE_INFINITY;
            if (aAt !== bAt)
                return aAt - bAt;
            return String(a?.name || '').localeCompare(String(b?.name || ''));
        });
    }

    /**
     * Sort phases by start ascending, then stop (undated last).
     * Optional milestones resolve linked bounds for form values.
     */
    static sortPhases<T extends {
        name?: string;
        startAt?: any;
        stopAt?: any;
        startMilestone?: string | null;
        stopMilestone?: string | null;
    }>(phases: T[] = [], milestones: { _id?: string; at?: any }[] = []): T[] {
        const byId = new Map((milestones || []).map((m) => [String(m._id), m]));
        const resolve = (phase: T, which: 'start' | 'stop'): number | null => {
            const mid = which === 'start' ? phase.startMilestone : phase.stopMilestone;
            if (mid && byId.get(String(mid))?.at != null)
                return moment(byId.get(String(mid))!.at).valueOf();
            const raw = which === 'start' ? phase.startAt : phase.stopAt;
            if (raw != null && raw !== '')
                return moment(raw).valueOf();
            return null;
        };

        return [...(phases || [])].sort((a, b) => {
            const aStart = resolve(a, 'start');
            const aStop = resolve(a, 'stop');
            const bStart = resolve(b, 'start');
            const bStop = resolve(b, 'stop');
            const aKey = aStart ?? aStop ?? Number.POSITIVE_INFINITY;
            const bKey = bStart ?? bStop ?? Number.POSITIVE_INFINITY;
            if (aKey !== bKey)
                return aKey - bKey;
            const aEnd = aStop ?? Number.POSITIVE_INFINITY;
            const bEnd = bStop ?? Number.POSITIVE_INFINITY;
            if (aEnd !== bEnd)
                return aEnd - bEnd;
            return String(a?.name || '').localeCompare(String(b?.name || ''));
        });
    }

    static allStatus() : string[] {
        return [
            Goal.STATUS_FINISHED,
            Goal.STATUS_RUNNING,
            Goal.STATUS_COMING
        ];
    }

    statusIndex(status?: string) {
        return Goal.allStatus().indexOf(status || this.status());
    }

    statusColor() {
        const index = this.statusIndex();
        return [
            'yellow',
            'blue',
            'green',
            'red'
        ][index];
    }

    status(): string {
        const now = moment();
        if (this.stopAt.isBefore(now))
            return Goal.STATUS_FINISHED;

        if (this.startAt.isAfter(now))
            return Goal.STATUS_COMING;

        return Goal.STATUS_RUNNING;
    }

    totalEstimated(changes?: Change[]) {
        return (changes || this.changes || []).reduce((acc: number, change: Change) => {
            return acc + change.estimate;
        }, 0);
    }

    progression(changes?: Change[]) : number {
        const list = changes || this.changes || [];
        const totalEstimated = this.totalEstimated(list);
        if (totalEstimated === 0)
            return 0;

        const totalRealised = list.reduce((acc: number, change: Change) => {
            return acc + (
                change.estimate * change.progression() / 100
            );
        }, 0);

        return totalRealised / totalEstimated * 100;
    }

    getPhase(phaseId?: string | null): GoalPhase | undefined {
        if (!phaseId)
            return undefined;
        return (this.phases || []).find((p) => p._id === phaseId);
    }

    /** Calendar elapsed between two dates, clamped to 0–100. */
    static elapsedPercent(start?: Moment, stop?: Moment, now: Moment = moment()): number | null {
        if (!start || !stop || !start.isValid() || !stop.isValid())
            return null;
        const span = stop.valueOf() - start.valueOf();
        if (span <= 0)
            return null;
        const ratio = (now.valueOf() - start.valueOf()) / span;
        return Math.round(Math.min(1, Math.max(0, ratio)) * 100);
    }

    /**
     * Calendar span covering the goal, its phases, milestones and card deadlines.
     */
    planningRange(): { start: Moment; stop: Moment } {
        const stamps: number[] = [];
        if (this.startAt?.isValid())
            stamps.push(this.startAt.valueOf());
        if (this.stopAt?.isValid())
            stamps.push(this.stopAt.valueOf());
        (this.milestones || []).forEach((milestone) => {
            if (milestone.at?.isValid())
                stamps.push(milestone.at.valueOf());
        });
        (this.phases || []).forEach((phase) => {
            if (phase.startAt?.isValid())
                stamps.push(phase.startAt.valueOf());
            if (phase.stopAt?.isValid())
                stamps.push(phase.stopAt.valueOf());
        });
        (this.changes || []).forEach((change) => {
            if (change.deadline?.isValid())
                stamps.push(change.deadline.valueOf());
        });
        if (!stamps.length)
            return { start: this.startAt, stop: this.stopAt };

        const start = moment(Math.min(...stamps));
        let stop = moment(Math.max(...stamps));
        if (stop.valueOf() <= start.valueOf())
            stop = start.clone().add(1, 'day');
        return { start, stop };
    }

    changesForPhase(phaseId?: string | null): Change[] {
        if (!phaseId)
            return (this.changes || []).filter((c) => !c.phase);
        return (this.changes || []).filter((c) => c.phase === phaseId);
    }

    /** Serialize form values for API (ISO dates). */
    static serializeForApi(values: AnyObj): AnyObj {
        const milestones = Goal.sortMilestones((values.milestones || []).map((m: AnyObj) => ({
            _id: m._id || undefined,
            name: m.name,
            at: moment(m.at).toISOString()
        })));
        const phases = Goal.sortPhases((values.phases || []).map((p: AnyObj) => {
            const startMilestone = p.startMilestone || null;
            const stopMilestone = p.stopMilestone || null;
            return {
                _id: p._id || undefined,
                name: p.name,
                startAt: startMilestone
                    ? undefined
                    : (p.startAt ? moment(p.startAt).toISOString() : undefined),
                stopAt: stopMilestone
                    ? undefined
                    : (p.stopAt ? moment(p.stopAt).toISOString() : undefined),
                startMilestone,
                stopMilestone
            };
        }), milestones);

        return {
            ...values,
            startAt: values.startAt ? moment(values.startAt).toISOString() : values.startAt,
            stopAt: values.stopAt ? moment(values.stopAt).toISOString() : values.stopAt,
            autoAttachProjectChanges: Boolean(values.autoAttachProjectChanges),
            milestones,
            phases,
            projects: (values.projects || []).map((p: AnyObj) => p?._id || p),
            // changes are managed via change updates, not goal payload
            changes: undefined
        };
    }
}

export default Goal;
